/**
 * Mock License Service
 * In-memory mock backend for license management.
 * Implements all license endpoints without real CosmosDB.
 * Used during development until real backend is deployed.
 */

import { LICENSE_PLANS, generateLicenseKey } from './licenseTypes';

// --- In-memory store (seeded with one active license) ---

const SEED_LICENSE = {
  id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  tipoEntidad: 'license',
  partitionKey: 'license',
  licenseKey: 'LIC-TEST-ABCD-1234-WXYZ',
  userId: 'seed-user-001',
  userEmail: 'sergio@example.com',
  plan: 'pro',
  status: 'active',
  createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  expiresAt: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
  maxDevices: 3,
  activations: [
    {
      installationId: 'install-seed-001',
      deviceName: 'PC de Sergio',
      activatedAt: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
};

let licenseStore = [SEED_LICENSE];

// --- Helper ---

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function generateSessionToken() {
  return Array.from({ length: 64 }, () =>
    'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]
  ).join('');
}

// --- API Implementations ---

/**
 * POST /api/licenses/purchase
 * Mock checkout: creates a license without real payment.
 * @param {{ plan: string, userId: string, userEmail?: string }} body
 * @returns {{ licenseKey: string, plan: string, expiresAt: string, maxDevices: number }}
 */
export function purchaseLicense({ plan, userId, userEmail = '' }) {
  const planConfig = LICENSE_PLANS.find((p) => p.name === plan);
  if (!planConfig) {
    throw new Error(`Plan no válido: ${plan}`);
  }

  const now = new Date();
  const expiresAt = new Date(now);
  expiresAt.setFullYear(expiresAt.getFullYear() + 1);

  const license = {
    id: generateId(),
    tipoEntidad: 'license',
    partitionKey: 'license',
    licenseKey: generateLicenseKey(),
    userId,
    userEmail,
    plan: planConfig.name,
    status: 'active',
    createdAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    maxDevices: planConfig.maxDevices,
    activations: []
  };

  licenseStore.push(license);

  return {
    licenseKey: license.licenseKey,
    plan: license.plan,
    expiresAt: license.expiresAt,
    maxDevices: license.maxDevices
  };
}

/**
 * GET /api/licenses/user/:userId
 * @param {string} userId
 * @returns {Array<{licenseKey, plan, status, createdAt, expiresAt, maxDevices, activeDevices}>}
 */
export function getUserLicenses(userId) {
  return licenseStore
    .filter((lic) => lic.userId === userId)
    .map((lic) => ({
      licenseKey: lic.licenseKey,
      plan: lic.plan,
      status: lic.status,
      createdAt: lic.createdAt,
      expiresAt: lic.expiresAt,
      maxDevices: lic.maxDevices,
      activeDevices: lic.activations.length
    }));
}

/**
 * POST /api/licenses/activate
 * @param {{ licenseKey: string, installationId: string, deviceName: string }} body
 * @returns {{ success: boolean, plan?: string, expiresAt?: string, sessionToken?: string, sessionExpiresAt?: string, error?: string }}
 */
export function activateLicense({ licenseKey, installationId, deviceName }) {
  const license = licenseStore.find((l) => l.licenseKey === licenseKey);
  if (!license) {
    return { success: false, error: 'Licencia no encontrada' };
  }
  if (license.status !== 'active') {
    return { success: false, error: `Licencia ${license.status}` };
  }
  if (new Date(license.expiresAt) < new Date()) {
    license.status = 'expired';
    return { success: false, error: 'Licencia expirada' };
  }

  // Check existing activation for this device
  const existing = license.activations.find((a) => a.installationId === installationId);
  if (!existing) {
    if (license.activations.length >= license.maxDevices) {
      return { success: false, error: `Máximo de dispositivos alcanzado (${license.maxDevices})` };
    }
    license.activations.push({
      installationId,
      deviceName,
      activatedAt: new Date().toISOString()
    });
  }

  const sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  return {
    success: true,
    plan: license.plan,
    expiresAt: license.expiresAt,
    sessionToken: generateSessionToken(),
    sessionExpiresAt
  };
}

/**
 * POST /api/licenses/validate
 * @param {{ licenseKey: string, installationId: string }} body
 * @returns {{ valid: boolean, plan?: string, expiresAt?: string, sessionToken?: string, sessionExpiresAt?: string, reason?: string }}
 */
export function validateLicense({ licenseKey, installationId }) {
  const license = licenseStore.find((l) => l.licenseKey === licenseKey);
  if (!license) {
    return { valid: false, reason: 'Licencia no encontrada' };
  }
  if (license.status !== 'active') {
    return { valid: false, reason: `Licencia ${license.status}` };
  }
  if (new Date(license.expiresAt) < new Date()) {
    license.status = 'expired';
    return { valid: false, reason: 'Licencia expirada' };
  }

  const activation = license.activations.find((a) => a.installationId === installationId);
  if (!activation) {
    return { valid: false, reason: 'Dispositivo no activado' };
  }

  const sessionExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  return {
    valid: true,
    plan: license.plan,
    expiresAt: license.expiresAt,
    sessionToken: generateSessionToken(),
    sessionExpiresAt
  };
}

/**
 * POST /api/licenses/release
 * @param {{ licenseKey: string, installationId: string }} body
 * @returns {{ success: boolean }}
 */
export function releaseLicense({ licenseKey, installationId }) {
  const license = licenseStore.find((l) => l.licenseKey === licenseKey);
  if (!license) {
    return { success: false };
  }

  license.activations = license.activations.filter((a) => a.installationId !== installationId);
  return { success: true };
}

/**
 * GET /api/licenses/plans
 * @returns {Array} Available license plans
 */
export function getPlans() {
  return LICENSE_PLANS;
}

/**
 * Reset store to initial seed state (for testing)
 */
export function resetStore() {
  licenseStore = [{ ...SEED_LICENSE, activations: [...SEED_LICENSE.activations] }];
}
